import React from 'react';
import {useTranslation} from 'react-i18next';
import {USAGE_FILTER} from '../../portal_usage/portalUsageState';

/**
 * El encabezado de la pantalla: la secuencia 71 → 12 → 4 y los cuatro contadores.
 *
 * **Cuatro números, no uno.** El titular pelado ("4 de 71 clientes usan el portal") deprime y no
 * dice qué hacer. La secuencia separa dos problemas que se resuelven distinto: *no invitaste* —una
 * tarea de la agencia, que se hace de a muchos— y *invitaste y no prendió* —una conversación con
 * cada cliente—.
 *
 * Y los contadores **son filtros**: un número en un encabezado que no se puede seguir es una
 * pregunta sin respuesta.
 */
export function PortalUsageFunnel({funnel, totals, windowDays, moduleOffCount, blockedCount,
                                   active, onSelect}) {
    const {t} = useTranslation();

    const total = funnel.clients_total || 0;
    const withUsers = funnel.clients_with_portal_users || 0;
    const activeCount = funnel.clients_active || 0;
    const invited = funnel.clients_invited_unused || 0;
    const dormant = funnel.clients_dormant || 0;
    const withoutUsers = Math.max(0, total - withUsers);

    const chips = [
        {key: USAGE_FILTER.ACTIVE, tone: 'ok', n: activeCount,
            label: t('portal_usage.rank_chip_active', 'Activos')},
        {key: USAGE_FILTER.INVITED_UNUSED, tone: 'ia', n: invited,
            label: t('portal_usage.rank_chip_invited', 'Con acceso, sin usar')},
        {key: USAGE_FILTER.DORMANT, tone: 'warn', n: dormant,
            label: t('portal_usage.rank_chip_dormant', 'Sin actividad')},
        {key: USAGE_FILTER.NO_USERS, tone: 'muted', n: withoutUsers,
            label: t('portal_usage.rank_chip_no_users', 'Sin usuarios')},
    ];

    // Los dos últimos no están en el funnel del backend: aparecen sólo si hay filas así, para que
    // ningún cliente quede fuera de todos los filtros y sólo visible en "Todos".
    if (moduleOffCount > 0) {
        chips.push({key: USAGE_FILTER.MODULE_OFF, tone: 'muted', n: moduleOffCount,
            label: t('portal_usage.rank_chip_portal_off', 'Portal apagado')});
    }
    // Éste no es un estado: es ortogonal (un cliente activo por el chat puede tener la puerta de
    // los informes cerrada). Va igual al embudo porque sin chip el aviso de la fila existe pero no
    // se puede convocar, y es el único bucket donde el arreglo es un click.
    if (blockedCount > 0) {
        chips.push({key: USAGE_FILTER.REPORTS_BLOCKED, tone: 'warn', n: blockedCount,
            label: t('portal_usage.rank_chip_reports_blocked', 'Comparten y no lo ven')});
    }

    return (
        <div className="box pu-rank__head">
            <h1 className="pu-rank__title">
                {t('portal_usage.rank_headline', '{{active}} de {{total}} clientes usan el portal',
                    {active: activeCount, total})}
            </h1>
            <p className="pu-rank__sub">
                {buildSubtitle(t, {withUsers, activeCount, invited, dormant, withoutUsers})}
            </p>
            {/* Los términos se definen UNA vez para toda la pantalla, acá. No van como tooltip en el
                encabezado de la columna: `.pu-rank__head-row` es `aria-hidden`, así que un tooltip
                ahí sería invisible para un lector de pantalla. */}
            <p className="pu-rank__note">
                {t('portal_usage.rank_sub_queries_note',
                    '«Consultas» cuenta lo que tu cliente le pidió al chat, no los pasos que hace su '
                    + 'asistente para responder. «Ingresos a informes» cuenta cada vez que abrió uno '
                    + 'de los informes que le compartiste.')}
            </p>

            <VolumeTiles totals={totals} windowDays={windowDays} t={t}/>

            {/* `data-chips` lo consume el SCSS para elegir el reparto en pantallas angostas: con
                `auto-fit` solo, el navegador elige cuántas columnas ENTRAN y deja la última fila
                incompleta (6 chips daban «5+1» a 1024px). Son tres cantidades posibles. */}
            <div className="pu-rank__funnel" data-chips={chips.length}>
                {chips.map(chip => (
                    <button
                        key={chip.key}
                        type="button"
                        className={`pu-fn -${chip.tone}${active === chip.key ? ' -on' : ''}`}
                        aria-pressed={active === chip.key}
                        // Volver a tocar el filtro activo lo apaga: si el único camino de vuelta a
                        // "todos" fuera otro control, el usuario queda encerrado en el filtro.
                        onClick={() => onSelect(active === chip.key ? USAGE_FILTER.ALL : chip.key)}
                    >
                        <span className="pu-fn__n">{chip.n}</span>
                        <span className="pu-fn__l">{chip.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/**
 * La bajada del encabezado, compuesta por segmentos.
 *
 * Es UNA frase con CINCO contadores, y `count` de i18next es uno solo: no hay forma de pluralizar
 * los cinco desde una sola key — por eso decía «1 nunca entraron» y «Tienes 1 clientes» (Q-7 del
 * review del 31/08). Cada segmento va con su propia key `_one`/`_other`, que es el patrón que ya
 * usa `client_memory.brief.*`, y el del medio los recibe ya resueltos.
 *
 * `escapeValue: false` porque lo que se interpola son frases ya traducidas, no datos: sin eso, un
 * segmento con apóstrofo o comilla saldría como entidad HTML (gotcha conocido de `i18n.js`).
 * Ninguno lo tiene hoy, pero el que escriba el próximo copy no debería tener que saberlo.
 */
function buildSubtitle(t, {withUsers, activeCount, invited, dormant, withoutUsers}) {
    const intro = t('portal_usage.rank_sub_intro', {count: withUsers});
    const middle = t('portal_usage.rank_sub_middle', {
        active: t('portal_usage.rank_sub_active', {count: activeCount}),
        invited: t('portal_usage.rank_sub_invited', {count: invited}),
        dormant: t('portal_usage.rank_sub_dormant', {count: dormant}),
        interpolation: {escapeValue: false},
    });
    // Sin clientes por invitar la última frase no aplica: «Los otros 0 clientes todavía no tienen a
    // nadie invitado» es ruido, y encima en el único caso que sería una buena noticia.
    const tail = withoutUsers > 0 ? ` ${t('portal_usage.rank_sub_without', {count: withoutUsers})}` : '';
    return `${intro} ${middle}${tail}`;
}

/**
 * Los dos volúmenes de la agencia: cuánto le consultan al chat y cuánto entran a sus informes.
 *
 * **No son clickeables, y es a propósito.** La regla de esta pantalla: lo que cuenta CLIENTES es un
 * filtro (los chips de abajo); lo que cuenta VOLUMEN es de sólo lectura. Ponerlos en la misma fila
 * que los buckets del embudo enseñaría que son comparables, y no lo son.
 *
 * Sin barra de progreso: para un volumen de consultas no existe denominador, así que la barra sería
 * inventada. Cada número lleva en cambio SU denominador de clientes — un total suelto no dice si
 * son ocho clientes o uno solo muy hablador.
 */
function VolumeTiles({totals, windowDays, t}) {
    const data = totals || {};
    const queries = data.client_queries || 0;
    const reportViews = data.report_views || 0;
    const withQueries = data.clients_with_queries || 0;
    const withReportViews = data.clients_with_report_views || 0;
    const shared = data.shared_reports || 0;

    // Sin el bloque de totals (backend viejo, ventana de skew) no se dibuja nada: dos tiles en cero
    // afirmarían que la agencia no tiene uso, que es lo contrario de "todavía no lo sabemos".
    if (!totals) return null;

    const window = t('portal_usage.rank_kpi_window', 'últimos {{n}} días', {n: windowDays || 30});
    const average = withQueries ? Math.round(queries / withQueries) : 0;

    return (
        <div className="pu-rank__kpis">
            <div className="pu-rank__kpi -chat">
                <span className="pu-rank__kpi-lbl">
                    <i className="pu-dot -chat" aria-hidden="true"/>
                    {t('portal_usage.rank_kpi_chat_title', 'Consultas al chat')}
                </span>
                <span className="pu-rank__kpi-val">
                    {queries}<em>{window}</em>
                </span>
                <span className="pu-rank__kpi-sub">
                    {/* Una frase por número, cada una con su `count`: con una sola frase y dos números
                        no había plural posible, y en prod se leía «1 clientes». */}
                    {t('portal_usage.rank_kpi_chat_clients', '{{count}} clientes conversan con su IA',
                        {count: withQueries})}
                    {' · '}
                    {t('portal_usage.rank_kpi_chat_avg', '{{count}} consultas por cliente en promedio',
                        {count: average})}
                </span>
            </div>

            <div className="pu-rank__kpi -rep">
                <span className="pu-rank__kpi-lbl">
                    <i className="pu-dot -rep" aria-hidden="true"/>
                    {t('portal_usage.rank_kpi_reports_title', 'Ingresos a informes')}
                </span>
                <span className="pu-rank__kpi-val">
                    {reportViews}<em>{window}</em>
                </span>
                <span className="pu-rank__kpi-sub">
                    {t('portal_usage.rank_kpi_reports_clients', '{{count}} clientes entraron a sus informes',
                        {count: withReportViews})}
                    {' · '}
                    {t('portal_usage.rank_kpi_reports_shared', '{{count}} informes compartidos en total',
                        {count: shared})}
                </span>
            </div>
        </div>
    );
}

export default PortalUsageFunnel;
